/**
 * One-time codes (RFC 6238 TOTP) computed in the browser — v3.91.0,
 * "Don't ask for a code on this computer for 30 days".
 *
 * A trusted computer is enrolled as an extra TOTP second factor on the
 * administrator's Firebase account. Its shared secret is imported ONCE, at
 * enrollment, as a non-extractable WebCrypto HMAC key: page code can use the
 * key to compute a code but can never read the secret back out. Firebase
 * still verifies every code on its side, exactly as it does for an
 * authenticator app.
 *
 * Nothing here touches Firebase or storage; see trustedComputer.js.
 */

const BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/** RFC 4648 base32 → bytes. Ignores case, spaces and '=' padding; throws on any other character. */
export function base32ToBytes(text) {
  const clean = String(text || '').toUpperCase().replace(/[\s=]/g, '');
  if (!clean) throw new Error('Empty TOTP secret.');
  const out = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of clean) {
    const value = BASE32.indexOf(ch);
    if (value < 0) throw new Error('The TOTP secret is not valid base32.');
    buffer = (buffer << 5) | value;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >>> bits) & 0xff);
    }
  }
  return new Uint8Array(out);
}

/** Firebase's hashingAlgorithm ("SHA1", "SHA256", "SHA512", any case, with or without a dash) → WebCrypto's name. */
export function webCryptoHashName(algorithm) {
  const key = String(algorithm || 'SHA1').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const names = { SHA1: 'SHA-1', SHA256: 'SHA-256', SHA512: 'SHA-512' };
  if (!names[key]) throw new Error(`Unsupported TOTP hashing algorithm: ${algorithm}`);
  return names[key];
}

/** The browser's WebCrypto, or null where it is missing (an insecure origin, an old browser). */
export function defaultSubtle() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  return c && c.subtle ? c.subtle : null;
}

/** Imports raw secret bytes as a NON-extractable HMAC key that can only sign. */
export async function importTotpKey(secretBytes, hashName, subtle = defaultSubtle()) {
  if (!subtle) throw new Error('This browser cannot store a trusted-computer key.');
  return subtle.importKey('raw', secretBytes, { name: 'HMAC', hash: { name: hashName } }, false, ['sign']);
}

/**
 * The code for `nowMs` (RFC 6238 / RFC 4226 dynamic truncation).
 * `periodSeconds` and `digits` come from the enrollment (Firebase: 30 s, 6 digits).
 */
export async function totpCode(key, { nowMs, periodSeconds = 30, digits = 6 } = {}, subtle = defaultSubtle()) {
  if (!subtle) throw new Error('This browser cannot compute a code.');
  if (!Number.isFinite(nowMs)) throw new Error('A time is required.');
  if (!Number.isInteger(periodSeconds) || periodSeconds <= 0) throw new Error('Invalid TOTP period.');
  if (!Number.isInteger(digits) || digits < 6 || digits > 10) throw new Error('Invalid TOTP length.');
  const counter = Math.floor(nowMs / 1000 / periodSeconds);
  const message = new ArrayBuffer(8);
  const view = new DataView(message);
  view.setUint32(0, Math.floor(counter / 2 ** 32));
  view.setUint32(4, counter >>> 0);
  const mac = new Uint8Array(await subtle.sign('HMAC', key, message));
  const offset = mac[mac.length - 1] & 0x0f;
  const binary = ((mac[offset] & 0x7f) * 2 ** 24)
    + (mac[offset + 1] << 16)
    + (mac[offset + 2] << 8)
    + mac[offset + 3];
  return String(binary % 10 ** digits).padStart(digits, '0');
}
